import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

// Cancel invite response type
interface CancelInviteResponse {
  statusCode: number;
  message: string;
  data: { cancelled: boolean };
  timestamp: string;
}

import type {
  DisableEligibility,
  CreateAgencyRequest,
  CreateAgencyResponse,
  GetAgenciesResponse,
  GetAgencyByIdResponse,
  UpdateAgencyRequest,
  DeleteAgencyResponse,
  GetAgencyStatsResponse,
  GetAgencyStatsParams,
  //AddUserToAgencyRequest,
  AddUserToAgencyResponse,
  AddUserToAgencyParams,
  UpdateAgencyUserRoleParams,
  UpdateAgencyUserRoleResponse,
  GetAgencyUsersResponse,
  DeleteAgencyUserParams,
  DeleteAgencyUserResponse,
  GetPerformanceTrendsResponse,
  GetSubAgencyStatsParams,
  GetSubAgencyStatsResponse,
  GetSubAgentStatsParams,
  GetSubAgentStatsResponse,
  GetAgentStatsParams,
  GetAgentStatsResponse,
  MoveUserParams,
  MoveUserResponse,
} from "../types/types";

// Create a new agency
export const createAgency = async (data: CreateAgencyRequest) => {
  const url = getApiUrl(endpoint.agency.createAgency);
  
  try {
    const result = await handleApiCall<CreateAgencyResponse>(async () => {
      const response = await api.post<CreateAgencyResponse>({
        url,
        body: data,
        auth: true, // Add authentication
      });
      return response;
    });
    
    showToast("Agency created successfully!", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get all agencies
export const getAgencies = async () => {
  const url = getApiUrl(endpoint.agency.getAgencies);
  
  try {
    const result = await handleApiCall<GetAgenciesResponse>(async () => {
      const response = await api.get<GetAgenciesResponse>({
        url,
      });
      return response;
    });
    
    return result;
  } catch (error) {
    const transformedError = transformErrorResponse(error);
    
    // Handle 404 as empty result instead of error for agencies
    if (transformedError?.code === '404' || transformedError?.message?.includes('404')) {

      return {
        statusCode: 404,
        message: 'No agencies found',
        data: {
          meta: {
            page: 1,
            nextPage: null,
            prevPage: null,
            offset: 0,
            totalItems: 0,
            totalPages: 0,
            itemCount: 0,
          },
        },
        items: [],
        timestamp: new Date().toISOString(),
        timeTakenMs: 0,
        path: url,
      };
    }
    
    showToast(transformedError?.message || "Failed to fetch agencies", "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformedError;
  }
};

// Get agency by ID
export const getAgencyById = async (id: string) => {
  const url = getApiUrl(endpoint.agency.getAgencyById(id));
  
  try {
    const result = await handleApiCall<GetAgencyByIdResponse>(async () => {
      const response = await api.get<GetAgencyByIdResponse>({
        url,
      });
      return response;
    });
    
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Update agency
export const updateAgency = async (id: string, data: UpdateAgencyRequest) => {
  const url = getApiUrl(endpoint.agency.updateAgency(id));
  
  try {
    const response = await api.put({
      url,
      body: data,
    });
    
    showToast("Agency updated successfully!", "success");
    return response.data;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Delete agency
export const deleteAgency = async (id: string) => {
  const url = getApiUrl(endpoint.agency.deleteAgency(id));
  
  try {
    const result = await handleApiCall<DeleteAgencyResponse>(async () => {
      const response = await api.delete<DeleteAgencyResponse>({
        url,
      });
      return response;
    });
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get agency stats
export const getAgencyStats = async (params: GetAgencyStatsParams) => {
  const { agencyId, period = '6-months', startDate, endDate } = params;
  const queryParams = new URLSearchParams();
  queryParams.append('period', period);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  const url = `${getApiUrl(endpoint.agency.agencyStats(agencyId))}?${queryParams.toString()}`;

  try {
    const result = await handleApiCall<GetAgencyStatsResponse>(async () => {
      const response = await api.get<unknown>({
        url,
        auth: true, // Add authentication
      });
      return response;
    });
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Add user to agency
export const addUserToAgency = async (params: AddUserToAgencyParams) => {
  const { agencyId, data } = params;
  const url = getApiUrl(endpoint.agency.agencyUsers(agencyId));
  
  try {
    const result = await handleApiCall<AddUserToAgencyResponse>(async () => {
      const response = await api.post<AddUserToAgencyResponse>({
        url,
        body: data,
        auth: true, // Add authentication
      });
      return response;
    });
    
    showToast("User added to agency successfully!", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Create a new sub-agency
export const createSubAgency = async (data: CreateAgencyRequest) => {
  const url = getApiUrl(endpoint.agency.createSubAgency);

  try {
    const result = await handleApiCall<CreateAgencyResponse>(async () => {
      const response = await api.post<CreateAgencyResponse>({
        url,
        body: data,
        auth: true, // Add authentication
      });
      return response;
    });
    // Toast is handled by the calling component (CreateSubAgencyModal) for richer context
    return result;
  } catch (error) {

    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get sub-agencies (optionally with filters)
export const getSubAgencies = async (params?: {
  parentAgencyId?: string;
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}) => {
  // IMPORTANT: Without a parentId filter, the backend returns the newest 50
  // agencies GLOBALLY (sorted by createdAt DESC). On prod that means you see
  // recently-created agencies from other tenants, not your sub-agencies.
  // Always forward the parentId so the query scopes to the viewing agency.
  const qs = new URLSearchParams();
  if (params?.parentAgencyId) qs.set('parentId', params.parentAgencyId);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.search) qs.set('search', params.search);
  if (typeof params?.isActive === 'boolean') qs.set('isActive', String(params.isActive));
  const query = qs.toString();
  const url = getApiUrl(endpoint.agency.getSubAgencies) + (query ? `?${query}` : '');
  try {
    const result = await handleApiCall<unknown>(async () => {
      const response = await api.get<unknown>({
        url,
        auth: true, // Add authentication
      });
      return response;
    });
    return result;
  } catch (error) {
    const transformedError = transformErrorResponse(error);
    
    // Handle 404 as empty result instead of error for sub-agencies
    if (transformedError?.code === '404' || transformedError?.message?.includes('404')) {

      return [];
    }
    
    // For other errors, still show toast and throw
    showToast(transformedError?.message || "Failed to fetch sub-agencies", "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformedError;
  }
};

// Update agency user role
export const updateAgencyUserRole = async (params: UpdateAgencyUserRoleParams) => {
  const { agencyId, data } = params;
  const url = getApiUrl(endpoint.agency.updateAgencyUserRole(agencyId, data.userId));

  try {
    const result = await handleApiCall<UpdateAgencyUserRoleResponse>(async () => {
      const response = await api.put({
        url,
        body: { role: data.newRole },
        auth: true,
      });
      return response;
    });
    
    showToast("User role updated successfully!", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get agency users
export const getAgencyUsers = async (agencyId: string, includeSubAgencies?: boolean) => {
  const base = getApiUrl(endpoint.agency.getAgencyUsers(agencyId));
  // When true, the endpoint returns agents across this agency + all descendant
  // sub-agencies, each tagged with their own agency (for the "Agency" column).
  const url = includeSubAgencies ? `${base}?includeSubAgencies=true` : base;

  try {
    // Don't use handleApiCall here because it extracts only the 'data' field
    // We need the full response to get 'pendingInvites' as well
    const response = await api.get<GetAgencyUsersResponse>({
      url,
      auth: true,
    });

    // Return the full response so we can access both data and pendingInvites
    return response;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Can the viewer disable this agent outright, or only request it?
export const getDisableEligibility = async (agencyId: string, userId: string): Promise<DisableEligibility> => {
  const url = getApiUrl(endpoint.agency.disableEligibility(agencyId, userId));
  const result = await handleApiCall<{ data: DisableEligibility }>(async () => api.get({ url, auth: true }));
  return (result as any)?.data ?? (result as any);
};

// Owner of a non-primary agency asks InsuraCentral to disable the agent
export const requestDeactivation = async (params: { agencyId: string; userId: string; reason?: string }) => {
  const url = getApiUrl(endpoint.agency.requestDeactivation(params.agencyId, params.userId));
  try {
    return await handleApiCall<{ message: string; data: { requestId: string; alreadyPending: boolean } }>(async () =>
      api.post({ url, body: { reason: params.reason }, auth: true }),
    );
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Suspend / unsuspend / restore — all owner-only, all reversible.
export const suspendAgent = async (
  agencyId: string,
  userId: string,
  reason?: string,
  autoDisableAfterDays?: number | null,
) => {
  try {
    return await handleApiCall<{ message: string }>(async () =>
      api.post({
        url: getApiUrl(endpoint.agency.suspendAgent(agencyId, userId)),
        body: { reason, autoDisableAfterDays: autoDisableAfterDays ?? null },
        auth: true,
      }));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

export const unsuspendAgent = async (agencyId: string, userId: string) => {
  try {
    return await handleApiCall<{ message: string }>(async () =>
      api.post({ url: getApiUrl(endpoint.agency.unsuspendAgent(agencyId, userId)), body: {}, auth: true }));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

export const restoreAgent = async (agencyId: string, userId: string) => {
  try {
    return await handleApiCall<{ message: string; data: { notes: string[] } }>(async () =>
      api.post({ url: getApiUrl(endpoint.agency.restoreAgent(agencyId, userId)), body: {}, auth: true }));
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Delete agency user
export const deleteAgencyUser = async (params: DeleteAgencyUserParams) => {
  const url = getApiUrl(endpoint.agency.deleteAgencyUser(params.agencyId));
  
  try {
    const result = await handleApiCall<DeleteAgencyUserResponse>(async () => {
      const response = await api.delete<DeleteAgencyUserResponse>({
        url,
        body: params.data,
        auth: true, // Add authentication
      });
      return response;
    });
    
    showToast(
      params.data.disableAccount ? "Account disabled and removed from agency" : "User removed from agency successfully!",
      "success",
    );
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Move user between agencies (with data transfer)
export const moveUser = async (params: MoveUserParams) => {
  const url = getApiUrl(endpoint.agency.moveUser(params.sourceAgencyId));

  try {
    const result = await handleApiCall<MoveUserResponse>(async () => {
      const response = await api.post<MoveUserResponse>({
        url,
        body: params.data,
        auth: true,
      });
      return response;
    });

    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Bulk-move several agents from one agency to a target in one request.
export interface BulkMoveUsersResult {
  sourceAgencyId: string;
  targetAgencyId: string;
  movedCount: number;
  total: number;
  totalLeads: number;
  results: { userId: string; status: "moved" | "skipped" | "failed"; leadsMoved?: number; reason?: string }[];
}
export const moveUsersBulk = async (params: {
  sourceAgencyId: string;
  targetAgencyId: string;
  userIds: string[];
  role?: string;
}) => {
  // URL built inline (not via the endpoint registry) so this brand-new call
  // doesn't depend on the endpoints module having hot-reloaded in dev.
  const url = getApiUrl(`/agencies/${params.sourceAgencyId}/move-users-bulk`);
  try {
    return await handleApiCall<BulkMoveUsersResult>(async () => {
      return await api.post<BulkMoveUsersResult>({
        url,
        body: { targetAgencyId: params.targetAgencyId, userIds: params.userIds, role: params.role },
        auth: true,
      });
    });
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

export const getPerformanceTrends = async (
  agencyId: string,
  period: '6-months' | '12-months' | 'year-to-date' = '6-months',
  startDate?: string,
  endDate?: string
) => {
  const url = getApiUrl(endpoint.agency.getPerformanceTrends(agencyId));

  try {
    const params: Record<string, string> = { period };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const result = await handleApiCall<GetPerformanceTrendsResponse>(async () => {
      const response = await api.get<GetPerformanceTrendsResponse>({
        url,
        params,
        auth: true, // Add authentication
      });
      return response;
    });

    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw transformErrorResponse(error);
  }
};

// Get sub-agency stats
export const getSubAgencyStats = async (params: GetSubAgencyStatsParams) => {
  const { agencyId, period = "6-months", startDate, endDate, dryRun = false } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (dryRun) queryParams.append("dryRun", "true");

  // Use the sub-agency stats endpoint to get stats for each sub-agency
  const url = getApiUrl(`${endpoint.agency.getSubAgencyStats(agencyId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<GetSubAgencyStatsResponse>(async () => {
      const response = await api.get<GetSubAgencyStatsResponse>({
        url,
        auth: true,
      });
      return response;
    });

    // Handle nested data structure - dashboard overview returns the metrics directly
    return result?.data || result || {};
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
};

// A node in the sub-agency tree with subtree-rolled-up stats.
export interface SubAgencyTreeNode {
  id: string;
  name: string;
  location?: string;
  parentAgencyId: string | null;
  isActive: boolean;
  directAgents: number;
  directPremium: number;
  directIssuedPremium?: number;
  directPolicies: number;
  totalAgents: number;
  totalPremium: number;
  /** Sold & Approved only; totalPremium is gross incl. Underwriting. */
  issuedPremium?: number;
  totalPolicies: number;
  averagePolicySize: number;
  closingRatio: number;
  changePercentage: number;
}

// Get the full sub-agency TREE with premium/policies/agents rolled up across
// every descendant depth (powers the expandable view + drill-in premium pie).
export const getSubAgencyTreeStats = async (params: {
  agencyId: string;
  period?: "6-months" | "12-months" | "year-to-date";
  startDate?: string;
  endDate?: string;
}): Promise<SubAgencyTreeNode[]> => {
  const { agencyId, period = "6-months", startDate, endDate } = params;
  const qp = new URLSearchParams();
  qp.append("period", period);
  if (startDate) qp.append("startDate", startDate);
  if (endDate) qp.append("endDate", endDate);
  const url = getApiUrl(`${endpoint.agency.getSubAgencyTreeStats(agencyId)}?${qp.toString()}`);
  const result = await handleApiCall<any>(async () => api.get<any>({ url, auth: true }));
  const data = result?.data ?? result ?? [];
  return Array.isArray(data) ? (data as SubAgencyTreeNode[]) : [];
};

// Get sub-agent stats
// Get agent stats for agency agents (individual performance)
export const getAgentStats = async (params: GetAgentStatsParams) => {
  const { agencyId, period = "6-months", startDate, endDate, dryRun = false } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (dryRun) queryParams.append("dryRun", "true");

  const url = getApiUrl(`${endpoint.agency.getAgentStats(agencyId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<GetAgentStatsResponse>(async () => {
      const response = await api.get<GetAgentStatsResponse>({
        url,
        auth: true,
      });
      return response;
    });

    // Extract the data array from the response
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as GetAgentStatsResponse).data || [];
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
};

export const getSubAgentStats = async (params: GetSubAgentStatsParams) => {
  const { agencyId, period = "6-months", dryRun = false } = params;
  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (dryRun) queryParams.append("dryRun", "true");

  const url = getApiUrl(`${endpoint.agency.getSubAgentStats(agencyId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<GetSubAgentStatsResponse>(async () => {
      const response = await api.get<GetSubAgentStatsResponse>({
        url,
        auth: true,
      });
      return response;
    });

    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
};

// Get individual user stats (for any agency member - agent, owner, admin)
export interface GetIndividualUserStatsParams {
  agencyId: string;
  userId: string;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
}

export interface IndividualUserStats {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  contactPhone?: string | null;
  accountType?: string;
  title?: string | null;
  role?: string;
  totalPremium: number;
  /** Sold & Approved only. totalPremium is gross and includes Underwriting. */
  issuedPremium?: number;
  totalPolicies: number;
  /** Sold & Approved only. totalPolicies also counts Underwriting. */
  issuedPolicies?: number;
  closingRate: number;
  changePercentage: number;
  isOnline: boolean;
  userStatus?: 'available' | 'idle' | 'on_call' | 'dnd' | 'offline';
  dialTime: number;
  activeLeads: number;
  topProducts: Array<{ product: string; carrier: string; count: number; totalPremium: number; issuedPremium?: number }>;
  monthlyTrends: Array<{ month: string; premium: number; issuedPremium?: number; policies: number }>;
}

export const getIndividualUserStats = async (params: GetIndividualUserStatsParams): Promise<IndividualUserStats> => {
  const { agencyId, userId, period = "6-months", startDate, endDate } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const url = getApiUrl(`${endpoint.agency.getIndividualUserStats(agencyId, userId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<{ data: IndividualUserStats }>(async () => {
      const response = await api.get<{ data: IndividualUserStats }>({
        url,
        auth: true,
      });
      return response;
    });

    // Extract the data from the response
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: IndividualUserStats }).data;
    }
    return result as IndividualUserStats;
  } catch (error) {
    // Don't show toast for user stats - let the component handle the error display
    throw transformErrorResponse(error);
  }
};

// Get premium breakdown by carrier for pie chart
export interface CarrierPremiumData {
  carrier: string;
  totalPremium: number;
  policyCount: number;
  percentage: number;
}

export interface GetPremiumByCarrierParams {
  agencyId: string;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
}

export const getPremiumByCarrier = async (params: GetPremiumByCarrierParams): Promise<CarrierPremiumData[]> => {
  const { agencyId, period = "6-months", startDate, endDate } = params;

  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const url = getApiUrl(`${endpoint.agency.getPremiumByCarrier(agencyId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<{ data: CarrierPremiumData[] }>(async () => {
      const response = await api.get<{ data: CarrierPremiumData[] }>({
        url,
        auth: true,
      });
      return response;
    });

    // Extract the data from the response
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: CarrierPremiumData[] }).data;
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    // Don't show toast - let the component handle display
    throw transformErrorResponse(error);
  }
};

// ---- Dial Efficiency (call-effort economics) ----
export interface DialEffortSegment {
  band: string;
  leads: number;
  dials: number;
  appointments: number;
  sales: number;
  policiesSold: number;
  premium: number;
  apptsPer100Dials: number;
  dialsPerAppointment: number | null;
}

export interface DialEfficiencyStateRow {
  state: string;
  leads: number;
  dials: number;
  connects: number;
  contactRate: number;
  appointments: number;
  sales: number;
  policiesSold: number;
  premium: number;
  /** Dials per POLICY sold (platform vocabulary). */
  dialsPerSale: number | null;
  dialsPerAppointment: number | null;
  premiumPer100Dials: number;
}

export interface DialEfficiencyData {
  window: { startDate: string; endDate: string; period: string };
  summary: {
    leads: number;
    dials: number;
    connects: number;
    leadsContacted: number;
    contactRate: number;
    leadsWithAppointment: number;
    sales: number;
    policiesSold: number;
    premium: number;
    productionPremium: number;
    producingLeads: number;
    avgDialsFirst7Days: number;
    pctLeadsWith9PlusDials7d: number;
    medianSpeedToLeadMinutes: number | null;
  };
  attemptCurve: Array<{
    attempt: number;
    leadsReached: number;
    connects: number;
    connectRate: number;
    cumulativeContactedPct: number;
  }>;
  attemptsToAppointment: Array<{ attempts: number; appointments: number }>;
  byAge: DialEffortSegment[];
  byGender: DialEffortSegment[];
  byState: DialEfficiencyStateRow[];
  speedToLead: Array<{
    bucket: string;
    leads: number;
    contacted: number;
    contactedPct: number;
    appointments: number;
    sales: number;
  }>;
}

export const getDialEfficiency = async (
  params: GetPremiumByCarrierParams & { agentId?: string },
): Promise<DialEfficiencyData | null> => {
  const { agencyId, period = "6-months", startDate, endDate, agentId } = params;
  const queryParams = new URLSearchParams();
  queryParams.append("period", period);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (agentId) queryParams.append("agentId", agentId);

  const url = getApiUrl(`${endpoint.agency.getDialEfficiency(agencyId)}?${queryParams.toString()}`);

  try {
    const result = await handleApiCall<{ data: DialEfficiencyData | null }>(async () => {
      const response = await api.get<{ data: DialEfficiencyData | null }>({
        url,
        auth: true,
      });
      return response;
    });
    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: DialEfficiencyData | null }).data;
    }
    return (result as DialEfficiencyData) ?? null;
  } catch (error) {
    // Don't show toast - let the component handle display
    throw transformErrorResponse(error);
  }
};

// Get policy statistics by state for agency hierarchy (policy map)
export interface AgencyStateStats {
  state: string;
  count: number;
  totalPremium: number;
  topCarrier: string;
}

export const getAgencyPolicyStatsByState = async (
  agencyId: string,
  period: string = '6-months',
  startDate?: string,
  endDate?: string,
): Promise<AgencyStateStats[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append('period', period);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  const url = `${getApiUrl(endpoint.agency.getPolicyStatsByState(agencyId))}?${queryParams.toString()}`;

  try {
    const result = await handleApiCall<{ data: AgencyStateStats[] }>(async () => {
      const response = await api.get<{ data: AgencyStateStats[] }>({
        url,
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: AgencyStateStats[] }).data;
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

// Get lead statistics by state for agency hierarchy (lead map)
export interface AgencyLeadStateStats {
  state: string;
  count: number;
  unassignedCount: number;
  assignedCount: number;
}

export const getAgencyLeadStatsByState = async (
  agencyId: string,
  period: string = '6-months',
  startDate?: string,
  endDate?: string,
): Promise<AgencyLeadStateStats[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append('period', period);
  if (startDate) queryParams.append('startDate', startDate);
  if (endDate) queryParams.append('endDate', endDate);
  const url = `${getApiUrl(endpoint.agency.getLeadStatsByState(agencyId))}?${queryParams.toString()}`;

  try {
    const result = await handleApiCall<{ data: AgencyLeadStateStats[] }>(async () => {
      const response = await api.get<{ data: AgencyLeadStateStats[] }>({
        url,
        auth: true,
      });
      return response;
    });

    if (result && typeof result === 'object' && 'data' in result) {
      return (result as { data: AgencyLeadStateStats[] }).data;
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    throw transformErrorResponse(error);
  }
};

// Cancel a pending invite
export const cancelInvite = async (inviteId: string) => {
  const url = getApiUrl(endpoint.invites.cancelInvite(inviteId));

  try {
    const result = await handleApiCall<CancelInviteResponse>(async () => {
      const response = await api.delete<CancelInviteResponse>({
        url,
        auth: true,
      });
      return response;
    });

    showToast("Invite cancelled successfully!", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
};
